import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AppWindow, HardDrive, ShieldCheck } from 'lucide-react';
import ITApplicationManagement from '../it-application/ITApplicationManagement';
import ITAssetManagement from '../it-asset/ITAssetManagement';
import ITGCAccessManagement from './ITGCAccessManagement';

type Section = 'applications' | 'assets' | 'access';

const ITEMS: { key: Section; label: string; icon: React.ElementType }[] = [
  { key: 'applications', label: 'IT Applications', icon: AppWindow   },
  { key: 'assets',       label: 'IT Assets',        icon: HardDrive   },
  { key: 'access',       label: 'Access Rights',    icon: ShieldCheck },
];

// Lightweight catalogue (key + label) so the global header search can index
// every ITGC section and deep-link to it via ?tab=itgc&section=<key>. Single
// source of truth — derived from ITEMS.
export const ITGC_SECTIONS: Array<{ key: Section; label: string }> =
  ITEMS.map(({ key, label }) => ({ key, label }));

const ITGCHub: React.FC = () => {
  // Honour an initial ?section=<key> deep-link (e.g. the global header search
  // navigates to a specific ITGC section) — fall back to the first section when
  // absent or unknown.
  const [params] = useSearchParams();
  const initialSection = ITEMS.find(i => i.key === params.get('section'))?.key ?? 'applications';
  const [active, setActive] = useState<Section>(initialSection);

  // React to ?section=<key> changes that arrive after mount (e.g. the global
  // header search navigates here while ITGC is already open — the URL changes
  // but this component does not remount).
  const sectionParam = params.get('section');
  useEffect(() => {
    const match = ITEMS.find(i => i.key === sectionParam);
    if (match) setActive(match.key);
  }, [sectionParam]);

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left nav card ──────────────────────────────────────────── */}
      <div className="netbody-card side-nav-card">
        <div className="side-nav-card-heading">ITGC</div>
        <nav aria-label="ITGC section navigation">
          {ITEMS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActive(key)}
              className={`side-panel-menu-item${active === key ? ' side-panel-menu-item--active' : ''}`}
              data-testid={`NAV_itgc_hub_${key}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Right card: renders active sub-component ───────────────── */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
        {active === 'applications' && <ITApplicationManagement compact />}
        {active === 'assets'       && <ITAssetManagement compact />}
        {active === 'access'       && <ITGCAccessManagement />}
      </div>

    </div>
  );
};

export default ITGCHub;
